import { ActionChip } from "@newsela/angelou";
import { NextRouter } from "next/router";
import { ReactNode } from "react";

import ContentLevel from "@/types/models/ContentLevel.interface";
import {
  CITATION_DEFAULT_WEBSITE_TITLE,
  CITATION_OPTION_VALUES,
} from "./CitationAction.constant";
import {
  ICitationAuthor,
  ICitationFormInput,
  IContentData,
  IFormatCitationDataResponse,
} from "./CitationAction.interface";
import { actionCss } from "./CitationAction.style";

interface MountCitationProps {
  tab: string;
  hasMissingFields: boolean;
  citationData: ICitationFormInput;
}

interface FormatCitationDataProps {
  authors: string[];
  publishedAt: string | undefined;
  isOriginalLevel: boolean | undefined;
}

export const formatDate = (baseDate: string | Date) => {
  const isString = typeof baseDate === "string";

  if (isString && !isDateValid(baseDate)) return "";

  const date = isString ? new Date(baseDate) : baseDate;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const mountActionChip = (text: string): ReactNode => (
  <ActionChip
    text={text}
    colorOption="ORANGE"
    onClick={() => null}
    __cssFor={{ root: actionCss }}
  />
);

export const isDateValid = (dateString: string | undefined) => {
  if (!dateString) return false;

  const iso8601Regex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;

  if (!iso8601Regex.test(dateString)) return false;

  const date = Date.parse(dateString);

  return !isNaN(date);
};

const splitAuthor = (author: string) => {
  const splittedAuthor = author?.trim().replace(/\s+/g, " ").split(" ");

  const firstName = splittedAuthor[0] || null;

  // if splittedAuthor has more than one item, we get the last one.
  const lastName =
    splittedAuthor.length > 1
      ? splittedAuthor[splittedAuthor.length - 1] || null
      : null;

  const middleName =
    splittedAuthor.length > 2 ? splittedAuthor.slice(1, -1).join(" ") : null;

  // if splittedAuthor has more than one item, we get the initials for all except the last.
  const names =
    splittedAuthor.length > 1 ? splittedAuthor.slice(0, -1) : splittedAuthor;
  const initials = names
    .map((name) => name.charAt(0).toUpperCase() + ".")
    .join(" ");

  const response: ICitationAuthor = {
    firstName,
    middleName,
    lastName,
    initials,
    suffix: null,
  };

  return response;
};

export const formatCitationData = ({
  authors = [],
  publishedAt = "",
  isOriginalLevel = false,
}: FormatCitationDataProps): IFormatCitationDataResponse => {
  const authorsList = authors
    .filter(
      (author) => !["adapted by Newsela", "edited by Newsela"].includes(author),
    )
    .map(splitAuthor)
    .filter((item) => item.lastName !== null);
  const publishedDate = isDateValid(publishedAt) ? publishedAt : "";
  const prefix = isOriginalLevel ? "Edited" : "Adapted";
  const websiteTitle = `${prefix} by ${CITATION_DEFAULT_WEBSITE_TITLE} Staff`;

  return { authors: authorsList, publishedDate, websiteTitle };
};

export const mountCitation = ({
  citationData,
  tab,
  hasMissingFields = false,
}: MountCitationProps) => {
  let response: ReactNode;

  if (!hasMissingFields) {
    if (tab === CITATION_OPTION_VALUES.MLA.tab)
      response = mountMLA(citationData);
    else if (tab === CITATION_OPTION_VALUES.APA.tab)
      response = mountAPA(citationData);
    else if (tab === CITATION_OPTION_VALUES.CHICAGO.tab)
      response = mountChicago(citationData);
  } else {
    if (tab === CITATION_OPTION_VALUES.MLA.tab)
      response = mountMissingMLA(citationData);
    else if (tab === CITATION_OPTION_VALUES.APA.tab)
      response = mountMissingAPA(citationData);
    else if (tab === CITATION_OPTION_VALUES.CHICAGO.tab)
      response = mountMissingChicago(citationData);
  }

  return response;
};

const mountAuthorsText = (authors: ICitationAuthor[], citationTab: string) => {
  let formattedAuthors = "";
  const isAPA = citationTab === "APA";
  const authorDetail = isAPA ? "initials" : "firstName";
  const authorsConnector = isAPA ? ", & " : ", and ";

  if (authors.length) {
    const firstAuthor = authors[0];

    formattedAuthors = `${firstAuthor?.lastName}, ${firstAuthor?.[authorDetail]}`;

    if (authors.length > 1) {
      formattedAuthors += authorsConnector;
      const otherAuthors = authors.slice(1);
      formattedAuthors += otherAuthors
        .map((author) => `${author.firstName} ${author[authorDetail]}`)
        .join(authorsConnector);
    }
  }

  return formattedAuthors;
};

export const mountMLA = ({
  authors,
  title,
  url,
  websiteTitle,
  datePublished,
  dateAccessed,
  contentProvider,
  version,
}: ICitationFormInput) => {
  const formattedDatePublished = formatDate(datePublished);
  const formattedDateAccessed = formatDate(dateAccessed);

  const formattedAuthors = mountAuthorsText(authors, "MLA");

  const authorsText = formattedAuthors ? `${formattedAuthors}. ` : "";

  const response = `${authorsText}"${title}." ${contentProvider}. ${websiteTitle}, ${version}, ${formattedDatePublished}, ${url}. Accessed ${formattedDateAccessed}`;

  return response;
};

export const mountAPA = ({
  authors,
  title,
  url,
  websiteTitle,
  datePublished,
  isOriginalLevel,
  contentProvider,
  version,
}: ICitationFormInput) => {
  const formattedPublishedDate = formatDate(datePublished);
  const [day = "", month = "", year = ""] = formattedPublishedDate.split(" ");
  const formattedWebsiteTitle = isOriginalLevel
    ? websiteTitle.replace("Edited by", "Ed.")
    : websiteTitle.replace("Adapted by", "Ad.");

  const formattedAuthors = mountAuthorsText(authors, "APA");

  const authorsText = formattedAuthors ? `${formattedAuthors} ` : "";

  const response = `${authorsText}(${year}, ${month} ${day}). ${title}. ${contentProvider} (${formattedWebsiteTitle}. ${version}). Retrieved from ${url}`;

  return response;
};

export const mountChicago = ({
  authors,
  title,
  url,
  websiteTitle,
  dateAccessed,
  contentProvider,
  version,
  datePublished,
}: ICitationFormInput) => {
  const formattedDatePublished = formatDate(datePublished);
  const formattedDateAccessed = formatDate(dateAccessed);

  const [publishedDay = "", publishedMonth = "", publishedYear = ""] =
    formattedDatePublished.split(" ");
  const [accessedDay = "", accessedMonth = "", accessedYear = ""] =
    formattedDateAccessed.split(" ");

  const formattedAuthors = mountAuthorsText(authors, "Chicago");

  const authorsText = formattedAuthors ? `${formattedAuthors}, ` : "";

  const response = `${authorsText}"${title}." ${contentProvider}. ${websiteTitle}, ${version}, ${publishedMonth} ${publishedDay}, ${publishedYear}, ${accessedMonth} ${accessedDay}, ${accessedYear}. ${url}`;

  return response;
};

export const mountMissingMLA = ({
  authors,
  title,
  url,
  websiteTitle,
  contentProvider,
  version,
  datePublished,
  dateAccessed,
}: ICitationFormInput) => {
  const formattedAuthors = mountAuthorsText(authors, "MLA");

  const authorsText = formattedAuthors ? `${formattedAuthors}. ` : "";

  const resultTitle = title || mountActionChip("Title");

  const resultPublishedAt = datePublished || mountActionChip("Published Date");

  const resultUrl = url || mountActionChip("Url");

  const formattedDateAccessed = formatDate(dateAccessed);

  const response = (
    <span>
      {authorsText}"{resultTitle}." {contentProvider}. {websiteTitle}, {version}
      , {resultPublishedAt}, {resultUrl}. {"Accessed "} {formattedDateAccessed}
    </span>
  );

  return response;
};

export const mountMissingAPA = ({
  authors,
  title,
  url,
  websiteTitle,
  datePublished,
  version,
  contentProvider,
}: ICitationFormInput) => {
  let formatedPublishedDate = "";

  if (datePublished) {
    const formattedPublishedDate = formatDate(datePublished);
    const [day, month, year] = formattedPublishedDate.split(" ");
    formatedPublishedDate = `(${year || ""}, ${month || ""} ${day || ""})`;
  }

  const resultDate = datePublished
    ? formatedPublishedDate
    : mountActionChip("Published Date");

  const formattedAuthors = mountAuthorsText(authors, "APA");

  const authorsText = formattedAuthors ? `${formattedAuthors} ` : "";

  const resultUrl = url || mountActionChip("Url");

  const formattedWebsiteTitle = websiteTitle.replace("Edited by", "Ed.");

  const response = (
    <span>
      {authorsText}
      {resultDate}. {title}. {contentProvider}{" "}
      {`(${formattedWebsiteTitle}. ${version}). Retrieved from `} {resultUrl}
    </span>
  );

  return response;
};

export const mountMissingChicago = ({
  authors,
  title,
  url,
  websiteTitle,
  contentProvider,
  version,
  datePublished,
  dateAccessed,
}: ICitationFormInput) => {
  const formattedDatePublished = formatDate(datePublished);
  const formattedDateAccessed = formatDate(dateAccessed);

  const [publishedDay = "", publishedMonth = "", publishedYear = ""] =
    formattedDatePublished.split(" ");
  const [accessedDay = "", accessedMonth = "", accessedYear = ""] =
    formattedDateAccessed.split(" ");

  const formattedAuthors = mountAuthorsText(authors, "Chicago");

  const authorsText = formattedAuthors ? `${formattedAuthors}, ` : "";

  const publishedDate = formattedDatePublished
    ? `${publishedMonth} ${publishedDay}, ${publishedYear}`
    : mountActionChip("Published Date");

  const response = (
    <span>
      {authorsText}"{title}." {contentProvider}. {websiteTitle}, {version},{" "}
      {publishedDate}, {accessedMonth} {accessedDay}, {accessedYear}. {url}
    </span>
  );

  return response;
};

export const setUpCitationState = (
  contentData: IContentData,
  currentLevel: ContentLevel | undefined,
  router: NextRouter,
  lastPublication: string = "",
): ICitationFormInput => {
  const {
    author: initialAuthor = "",
    display_published_at: displayPublishedAt = "",
    content_provider: provider,
  } = contentData;

  const {
    title = contentData.title || "",
    raw_lexile: rawLexile = 0,
    is_original_level: isOriginalLevel = true,
  } = currentLevel || {};

  const publishedAt = displayPublishedAt || lastPublication;

  const splittedAuthors = initialAuthor?.split(/, |\s* and \s*/) || [];

  const {
    authors: formattedAuthors,
    publishedDate: formattedPublishedDate,
    websiteTitle: formattedWebsiteTitle,
  } = formatCitationData({
    authors: splittedAuthors,
    publishedAt,
    isOriginalLevel,
  });

  return {
    authors: formattedAuthors,
    dateAccessed: new Date().toISOString(),
    datePublished: formattedPublishedDate,
    title,
    version: rawLexile === 0 ? "Original version" : `Version ${rawLexile}`,
    url: `${window.location.origin}${router.asPath}`,
    websiteTitle: formattedWebsiteTitle,
    contentProvider: provider
      ? `${provider.name} via ${CITATION_DEFAULT_WEBSITE_TITLE}`
      : "",
    isOriginalLevel,
  };
};
